/* eslint-disable react/display-name */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React from 'react';
import Typography from '@material-ui/core/Typography';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import ReportLink from 'components/ReportsModule/components/common/ReportLink';
import ReportCard from 'components/ReportsModule/components/common/ReportCard';
import LwtTable, { ColumnProps } from 'components/@common/Table/LwtTable';
import { KeyClassroomEngagementStudentResult } from 'types/schema-generated-types';
import { ReportEntityType } from 'components/ReportsModule/types';

const getScheduledActivityColumns: (t: TFunction) => ColumnProps<any>[] = t => [
  {
    id: 'lastName',
    label: t('Student'),
    sortKey: 'student.lastName',
    getValue: item => (
      <ReportLink
        key={item.student.id}
        entityType={ReportEntityType.STUDENT}
        entityId={item.student.id}
      >
        {`${item.student.firstName} ${item.student.lastName}`}
      </ReportLink>
    ),
    sortable: true
  },
  {
    id: 'gradeLevel',
    label: t('Grade'),
    helpText: t('gradeLevel', { ns: 'ReportsHelpCommon' }),
    sortKey: 'student.gradeLevel',
    getValue: item => <Typography>{t(item.student.gradeLevel, { ns: 'GradeLevel' })}</Typography>,
    sortable: true
  },
  {
    id: 'curriculumWeek',
    label: t('Current Curriculum Week'),
    helpText: t('curriculumWeek', { ns: 'ReportsHelpCommon' }),
    sortKey: 'curriculumWeek',
    getValue: item => (
      <Typography>
        {item.curriculumWeek ? t('Week {{weekNumber}}', { weekNumber: item.curriculumWeek }) : '-'}
      </Typography>
    ),
    sortable: true
  },
  {
    id: 'assessmentCompletedCount',
    label: t('Assessments Completed'),
    helpText: t('keyboardingAssessmentsCompleted', { ns: 'ReportsHelpTableColumns' }),
    sortKey: 'scheduledActivities.assessmentCompletedCount',
    getValue: item => (
      <Typography>{item.scheduledActivities?.assessmentCompletedCount ?? 0}</Typography>
    ),
    sortable: true
  },
  {
    id: 'assessmentSkippedCount',
    label: t('Assessments Skipped'),
    helpText: t('keyboardingAssessmentsSkipped', { ns: 'ReportsHelpTableColumns' }),
    sortKey: 'scheduledActivities.assessmentSkippedCount',
    getValue: item => (
      <Typography>{item.scheduledActivities?.assessmentSkippedCount ?? 0}</Typography>
    ),
    sortable: true
  },
  {
    id: 'assessmentAverageWpm',
    label: t('Average Speed (WPM)'),
    helpText: t('wpmAssessment', { ns: 'ReportsHelpCommon' }),
    sortKey: 'scheduledActivities.assessmentAverageWpm',
    getValue: item => (
      <Typography>{item.scheduledActivities?.assessmentAverageWpm ?? '-'}</Typography>
    ),
    sortable: true
  },
  {
    id: 'assessmentAverageAccuracy',
    label: t('Average Accuracy (%)'),
    helpText: t('accuracyAssessment', { ns: 'ReportsHelpCommon' }),
    sortKey: 'scheduledActivities.assessmentAverageAccuracy',
    getValue: item => (
      <Typography>
        {item.scheduledActivities?.assessmentAverageAccuracy
          ? `${item.scheduledActivities.assessmentAverageAccuracy}%`
          : '-'}
      </Typography>
    ),
    sortable: true
  }
];

const ScheduledActivityTableCard: React.FC<{
  reportData: KeyClassroomEngagementStudentResult[];
  loading: boolean;
}> = ({ reportData = [], loading }) => {
  const { t } = useTranslation([
    'Reports',
    'ReportsHelpCommon',
    'ReportsHelpTableColumns',
    'GradeLevel'
  ]);
  const columns = getScheduledActivityColumns(t);

  return (
    <ReportCard
      title={t('Teacher Assigned Assessments')}
      loading={loading}
      reportData={reportData}
      reportDataProp="scheduledActivities"
    >
      <LwtTable columns={columns} data={reportData} />
    </ReportCard>
  );
};

export default ScheduledActivityTableCard;
